import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { subscribeProviderFormInstances } from '../../utils/providerFormInstances';
import { useNavigate } from 'react-router-dom';
import { isDevAffordanceEnabled } from '../../utils/devMode';
import { useAuth } from '../../../hooks/useAuth';
import { db, functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { SearchIcon, ChevronRightIcon, RefreshIcon, UserIcon, CalendarIcon, LightningBoltIcon, TrashIcon, LockClosedIcon, ExclamationIcon } from '@heroicons/react/outline';
import { ChevronUpIcon, ChevronDownIcon, CheckCircleIcon } from '@heroicons/react/solid';
import { formatDateShort } from '../../../utils/timestampUtils';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import { apiGetReferralParticipants } from '../../utils/rmsApi';
import {
  calculateRoleStatus,
  getStatusBadgeConfig,
  classifyReferralStage,
  getTriageFormsProgress,
  buildFormProgressFromSnapshot,
  lastActivityLabel,
  referralStartedAt,
  isSenOnlyRecord,
  isHandoverComplete,
  hasSignedOffLetter,
  isAwaitingLetterSignOff,
  letterSignOffClinician,
  pinAwaitingLetterSignOff,
} from '../../utils/referralStatus';
import { isNonClinicalProvider } from '../../utils/providerRoles';
import GenerateDemoReferralsDialog from '../../components/provider/GenerateDemoReferralsDialog';
import RmsPatientRemoveForm from '../../components/provider/RmsPatientRemoveForm';
import { filterPatientsBySpecialism } from '../../utils/specialismMatch';
import {
  visiblePathways,
  patientInPathway,
  isAssignedToAnotherClinician,
  pathwayReviews,
  pathwayTriage,
  resolveActingPathway,
  PATHWAY,
  PATHWAY_LABELS,
} from '../../utils/triagePathways';
import {
  getDecisionAttribution,
  formatCliniciansInvolved,
} from '../../utils/decisionAttribution';
import { CLINICIAN_SPECIALISM, CLINICIAN_SPECIALISM_LABELS } from '../../../constants/userTypes';
import { isRiskStratificationVisible } from '../../constants/orgFeatures';
import useSessionState from '../../../hooks/useSessionState';
import { RMS_DASHBOARD_KEYS, coerceSubStage } from '../../utils/rmsDashboardViewState';
import ConcernBadge from '../../components/ConcernBadge';

// Environment check for showing dev tools (e.g. demo data generator)
const isDev = isDevAffordanceEnabled();

/**
 * RmsProviderDashboard
 * --------------------
 * Purpose:
 *   Provider-facing dashboard showing all CYP patient records for the organisation.
 *   This is the entry point for clinicians to view referrals and triage status.
 *
 * Layout:
 *   The dashboard uses a side-navigation layout where referrals are grouped into
 *   three overarching stages of the referral lifecycle:
 *
 *   A. Pre-Triage ("Incoming Referrals") — the not-ready side of the
 *      ready/not-ready split (LSC-038/LSC-044)
 *      - Forms In Progress (some requested form outstanding)
 *      - Forms Complete (everything submitted, awaiting the active decision to
 *        mark the referral Ready for triage)
 *
 *   B. Triage ("Ready for Triage") — only referrals actively marked ready
 *      - Pending 1st Review (a clinician can now triage)
 *      - 1st Triage = No, waiting for 2nd opinion
 *
 *   C. Post-Triage ("Triage Complete")
 *      - Discharged (two "No" outcomes)
 *      - Accepted onto Waiting List (at least one "Yes")
 *
 *   Each sub-stage shows a filtered table of referrals. The sidebar is always
 *   expanded (no collapsible accordion behaviour).
 *
 * Data:
 *   - Reads from rms_organisations/{orgId}/patientRecords
 *   - Real-time subscription for live updates
 */

/**
 * REFERRAL_STAGES
 * ---------------
 * Static configuration describing the three overarching referral stages and their
 * sub-stages. Each sub-stage has:
 *   - id:          unique key used for navigation state
 *   - label:       human-readable label displayed in both the sidebar and page header
 *   - description: short explanation shown under the page header for context
 *
 * The "stageNumber" on each sub-stage (1-7) reflects the logical ordering across
 * the entire referral lifecycle, making it easy to reason about progression.
 *
 * NOTE: The actual filtering logic (i.e. which referrals appear in which sub-stage)
 * has not been implemented yet. For now every sub-stage shows all referrals.
 */
const REFERRAL_STAGES = [
  {
    id: 'pre-triage',
    title: 'INCOMING REFERRALS',
    // One list, not a ready/not-ready split (Tom King, 23 Sept). A referral with
    // every form in moves itself to Pending 1st Review; nobody has to click to
    // promote it. Marking ready by hand remains, but only for a referral whose
    // forms are still outstanding and which the team wants seen anyway - the
    // reason for that is recorded (LSC-038/LSC-044).
    subStages: [
      {
        id: 'incomplete-referral-forms',
        label: 'Incoming Referrals',
        description: 'Referrals still being filled in. One can be marked Ready for triage from its overview, with the reason recorded.',
      },
    ],
  },
  {
    id: 'triage',
    title: 'READY FOR TRIAGE',
    subStages: [
      {
        id: 'pending-1st-review',
        label: 'Pending 1st Review',
      },
      {
        // The stored id stays 'pending-2nd-review' (session-restored tabs,
        // analytics); only the label changes (TDI-399).
        id: 'pending-2nd-review',
        label: 'Second Review',
      },
    ],
  },
  {
    id: 'post-triage',
    title: 'TRIAGE COMPLETE',
    // Three lists in this order (Tom King, 23 Sept), and a referral sits in
    // exactly one of them. Sign-off moves it to Admin Handover; finishing the
    // EMIS work and pressing Handover complete moves it on to Accepted or Not
    // Accepted (TDI-391). Sending the letter is a step within the handover,
    // not the end of it.
    subStages: [
      {
        id: 'ready-for-handover',
        label: 'Admin Handover',
        description:
          'With the admin team: referrals arrive once the outcome letter is signed '
          + 'off. Handover complete moves the referral to Accepted or Not accepted.',
      },
      {
        id: 'waiting-list',
        label: 'Accepted',
      },
      {
        // The stored id stays 'discharged-referrals' (session-restored tabs,
        // analytics); only the label avoids "Discharged" (LSC-015).
        id: 'discharged-referrals',
        label: 'Not Accepted',
      },
    ],
  },
  {
    id: 'stopped',
    title: 'STOPPED',
    // A withdrawn referral leaves every clinician's list and is never deleted
    // (TDI-472). The support side keeps one read-only place to find it, to
    // answer a family or the Trust (TDI-481, Tom King 24 Oct 2026). Only RMS
    // Support and non-clinical Org. Admins see this list; a clinician, or an
    // Org. Admin who is also an assignable clinician, never does.
    subStages: [
      {
        id: 'consent-withdrawn',
        label: 'Consent withdrawn',
        description:
          'Referrals the parent or carer withdrew consent from. Read only: nothing further '
          + 'happens on them unless the family gives consent again.',
      },
    ],
  },
];

// Flat list of every sub-stage id, in sidebar order. Used to validate a
// sub-stage tab restored from sessionStorage and as the default (first id).
const REFERRAL_SUBSTAGE_IDS = REFERRAL_STAGES.flatMap((stage) =>
  stage.subStages.map((sub) => sub.id),
);
const DEFAULT_SUBSTAGE = REFERRAL_SUBSTAGE_IDS[0]; // 'incomplete-referral-forms'

// Second Review exists only for the autism dual-review flow. The ADHD team
// makes a single MDT decision and never produces a second review, so the list
// is shown only while the autism pathway is in view, for every clinician
// (TDI-399).
//
// It used to be gated on specialism alone, which hid it from an ADHD-only
// clinician but left anyone covering both pathways looking at a permanently
// empty list under the ADHD tab. Gating on the pathway in view covers both: a
// single-specialism clinician only ever has their own pathway active.
export function isSubStageVisibleForPathway(subStageId, pathway) {
  if (subStageId !== 'pending-2nd-review') return true;
  return pathway === PATHWAY.ASD;
}

/** The sidebar label for a sub-stage id, for tests and for the nav. */
export function subStageLabel(subStageId) {
  for (const stage of REFERRAL_STAGES) {
    const found = stage.subStages.find((ss) => ss.id === subStageId);
    if (found) return found.label;
  }
  return null;
}

// Every list is visible to every provider user (Tom King, 23 Sept): clinicians
// and admins see the same board, so nobody has to ask where a referral went.
// Kept as an empty set rather than deleted so the intent is explicit and one
// entry restores role gating if that decision is revisited.
const SUPPORT_ONLY_SUBSTAGES = new Set([]);

// Lists for the non-clinical side only (TDI-481): RMS Support, and Org.
// Admins who are not assignable clinicians. Clinicians never see a withdrawn
// referral, and a Clinical Org. Admin is a clinician for this purpose.
const NON_CLINICAL_ONLY_SUBSTAGES = new Set(['consent-withdrawn']);

// Support-side operational visibility: the support team and org admins. Used
// for the admin-handover queue and for support-only columns (e.g. the
// last-activity dwell time on incomplete referrals, LSC-036) — clinicians
// don't chase forms, so they don't carry the extra column.
function isSupportSideUser(userType) {
  return userType === 'RMS Support' || userType === 'RMS Org. Admin';
}

export function isSubStageVisibleForRole(subStageId, claims) {
  if (NON_CLINICAL_ONLY_SUBSTAGES.has(subStageId)) return isNonClinicalProvider(claims);
  if (!SUPPORT_ONLY_SUBSTAGES.has(subStageId)) return true;
  return isSupportSideUser(claims?.userType);
}

function visibleSubStageIds(pathway, claims) {
  return REFERRAL_SUBSTAGE_IDS.filter(
    (id) => isSubStageVisibleForPathway(id, pathway) && isSubStageVisibleForRole(id, claims),
  );
}

// The ADHD team makes a single triage decision with no first/second split, so
// their "Pending 1st Review" tab reads simply "Pending Review". Other
// specialisms keep the numbered label because the autism dual-review flow still
// has a distinct 2nd review stage.
function subStageLabelForSpecialism(subStageId, defaultLabel, specialism) {
  if (
    subStageId === 'pending-1st-review' &&
    String(specialism || '').trim().toLowerCase() === CLINICIAN_SPECIALISM.ADHD
  ) {
    return 'Pending Review';
  }
  return defaultLabel;
}

// C8: the ADHD/autism (team) tabs only apply once a referral is in triage — the
// pre-triage stages are pre-classification and aren't split by pathway. The
// admin-handover queue is also unsplit: the support team processes all letters.
// Pathway tabs apply from triage onwards. Admin Handover is a post-triage list
// now, so it gets tabs like its neighbours; without them it silently defaulted
// to the ADHD view and an autism letter looked missing.
const PATHWAY_SCOPED_SUBSTAGES = new Set(
  REFERRAL_STAGES
    .filter((stage) => stage.id !== 'pre-triage')
    .flatMap((stage) => stage.subStages.map((ss) => ss.id)),
);

/**
 * Letter sign-off marks the clinician → admin handover point (agreed on the
 * 6 Aug 2026 BTH call): once a triage outcome letter is signed off it is
 * locked, and the remaining work (sending, entry into the clinical record) is
 * the admin team's. Combined referrals sign off one letter per pathway.
 * Returns one entry per signed-off letter on the record.
 */
function signedOffLetterEntries(patient) {
  const entries = [];
  if (patient?.triageDocuments?.signOff) {
    entries.push({ pathway: null, docs: patient.triageDocuments });
  }
  [PATHWAY.ADHD, PATHWAY.ASD].forEach((p) => {
    const docs = patient?.triagePathways?.[p]?.triageDocuments;
    if (docs?.signOff) entries.push({ pathway: p, docs });
  });
  return entries;
}

/** The most recent sign-off date on the record (for sorting/display). */
function latestSignOffAt(patient) {
  const dates = signedOffLetterEntries(patient)
    .map((e) => e.docs?.signOff?.signedOffAt)
    .map((ts) => ts?.toDate?.() || (ts ? new Date(ts) : null))
    .filter((d) => d && !Number.isNaN(d.getTime()));
  if (!dates.length) return null;
  return new Date(Math.max(...dates.map((d) => d.getTime())));
}

/**
 * RoleStatusBadge
 * ----------------
 * Renders the exact same badge styles used across service-user surfaces (e.g. ServiceUserDashboard,
 * PeoplePanel/PeopleDialog), by reusing `getStatusBadgeConfig`.
 */
const RoleStatusBadge = ({ status }) => {
  const { label, className } = getStatusBadgeConfig(status);
  return (
    <span className={`inline-flex min-w-28 items-center justify-center px-2 py-0.5 rounded-full border text-xs font-medium ${className}`}>
      {label}
    </span>
  );
};

/**
 * RiskTierBadge
 * ------------
 * Displays the provider's automated triage priority when available;
 * otherwise shows "Awaiting data".
 *
 * Props:
 *   tierLabel: Raw backend tier label (legacy "Risk" labels are supported)
 *   hasManualReview: If true, shows "Needs review" text below the badge
 *
 * Why normalization exists:
 *   Older records can still contain labels like "Medium Risk" / "Standard Risk".
 *   To keep language consistent across every table, we normalize all known values
 *   to the user-facing "X Priority" format before rendering.
 */
const RiskTierBadge = ({ tierLabel, hasManualReview }) => {
  // Show "Awaiting data" in italics when no tier is available yet
  // This occurs before any risk-relevant forms have been submitted
  if (!tierLabel) {
    return (
      <span className="text-xs text-gray-500 italic">
        Awaiting data
      </span>
    );
  }

  // Normalize legacy risk wording to consistent priority wording for display.
  const normalizedTierLabel = ({
    'Urgent Risk': 'Urgent Priority',
    'High Risk': 'High Priority',
    'Medium Risk': 'Medium Priority',
    'Standard Risk': 'Standard Priority',
  }[tierLabel]) || tierLabel;

  // Tier colors shared with clinician-determined priority badges in later stages.
  const tierColors = {
    'Urgent Priority': 'bg-red-100 text-red-800 border-red-200',
    'High Priority': 'bg-orange-100 text-orange-800 border-orange-200',
    'Medium Priority': 'bg-amber-100 text-amber-800 border-amber-200',
    'Standard Priority': 'bg-green-100 text-green-800 border-green-200',
  };

  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${tierColors[normalizedTierLabel] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {normalizedTierLabel}
    </span>
      {/* Show "Needs review" indicator when manual review items are pending */}
      {hasManualReview && (
        <span className="text-xs text-purple-600 italic">
          Needs review
        </span>
      )}
    </div>
  );
};

// Referrer type label – plain text, no badge styling
const ReferrerLabel = ({ type }) => {
  const isSchool = type?.toLowerCase() === 'school';
  return (
    <span className="text-sm text-gray-700">
      {isSchool ? 'School' : 'Parent / Carer'}
    </span>
  );
};

// Derive patient name from various possible field locations
const getPatientName = (record) => {
  const firstName = record.subject?.firstName || record.subject_first_name || record.firstName || '';
  const lastName = record.subject?.lastName || record.subject_last_name || record.lastName || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || 'Unknown Patient';
};

// Derive DOB from various possible field locations
const getPatientDob = (record) => {
  return record.subject?.dob || record.subject_dob || record.dob || null;
};

// Derive age — prefers a stored age, else computes whole years from the DOB.
const getPatientAge = (record) => {
  const explicit = record.subject?.age ?? record.age;
  if (explicit !== undefined && explicit !== null && explicit !== '') return explicit;
  const dob = getPatientDob(record);
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

/**
 * useCompletionDataForPatients
 * ----------------------------
 * Provider dashboard needs to show School + Parent/Carer form completion statuses that match
 * the service-user surfaces.
 *
 * We therefore:
 * - Fetch participant metadata (guardian invite status) via the same API used elsewhere.
 * - Subscribe to `formInstances` for each displayed patient to build a `formProgress` map.
 * - Compute `triageAssigned` based on the presence of any triage formInstance keys.
 *
 * Note: We intentionally scope listeners to the currently rendered patients (current page)
 * to avoid creating listeners for the entire dataset on large orgs.
 */
function useCompletionDataForPatients({ orgId, patientIds, recordsById = null }) {
  const [completionData, setCompletionData] = useState({});
  // The records themselves decide how their forms are read (the profile
  // flag and the progress mirror); kept on a ref so the listeners are not
  // rebuilt on every record stream.
  const recordsRef = useRef(recordsById);
  recordsRef.current = recordsById;

  // React Hook deps:
  // - `patientIds` is typically constructed from the currently rendered table rows,
  //   so it often changes identity between renders even when the *contents* are the same.
  // - We subscribe/unsubscribe Firestore listeners in this effect, so re-running it
  //   unnecessarily is expensive and can cause UI flicker.
  // - We therefore derive a stable string key that only changes when the ids change.
  const patientIdsKey = useMemo(
    () => JSON.stringify(Array.isArray(patientIds) ? patientIds.filter(Boolean) : []),
    [patientIds]
  );

  useEffect(() => {
    if (!orgId) return undefined;
    let ids = [];
    try {
      ids = JSON.parse(patientIdsKey);
    } catch (_) {
      ids = [];
    }
    if (ids.length === 0) return undefined;

    let cancelled = false;
    const unsubscribes = [];
    const participantDataRef = {};

    const run = async () => {
      try {
        // 1) Fetch participant metadata once (guardian invite status).
        await Promise.all(
          ids.map(async (patientRecordId) => {
            try {
              const { participants } = await apiGetReferralParticipants({ orgId, patientRecordId });
              const guardianParticipant = Array.isArray(participants)
                ? participants.find((p) => p.role === 'guardian' && p.status !== 'superseded')
                : null;
              participantDataRef[patientRecordId] = {
                hasGuardian: !!guardianParticipant,
                guardianInviteStatus: guardianParticipant?.status || null,
              };
            } catch (_) {
              participantDataRef[patientRecordId] = { hasGuardian: false, guardianInviteStatus: null };
            }
          })
        );

        if (cancelled) return;

        // 2) Subscribe to the form instances a provider may read for each
        // patient (TDI-574): the whole subcollection once the family has
        // shared the profile, otherwise the non-HFP instances one by one.
        ids.forEach((patientRecordId) => {
          const unsubscribe = subscribeProviderFormInstances(
            { orgId, patientRecordId, record: (recordsRef.current && recordsRef.current[patientRecordId]) || null },
            (qs) => {
              try {
                // Shared fold applies the completionPercent invariant (100 means
                // submitted; drafts capped at 99), keeping the provider dashboard
                // in step with every service-user view.
                const { formProgress, triageAssigned } = buildFormProgressFromSnapshot(qs);

                const participantData = participantDataRef[patientRecordId] || {};

                // Dwell time (LSC-036): the most recent form save across every
                // instance on the referral. Every save writes updatedAt, so
                // this is "when did anyone last work on their forms" — null
                // until a form exists.
                let lastFormActivityAt = null;
                qs.forEach((docSnap) => {
                  const d = docSnap.data() || {};
                  const ts = d.updatedAt || d.createdAt;
                  const millis = ts?.toMillis?.() ?? null;
                  if (millis && (!lastFormActivityAt || millis > lastFormActivityAt)) {
                    lastFormActivityAt = millis;
                  }
                });

                // GP-sharing consent (handover queue): the guardian's
                // lg_share_with_health answer on the request-for-referral
                // form — 'yes' | 'no' | 'not_sure', null until answered.
                let gpConsent = null;
                qs.forEach((docSnap) => {
                  if (docSnap.id !== 'requestForReferral_guardian') return;
                  const raw = docSnap.data()?.answers?.lg_share_with_health;
                  const value = Array.isArray(raw) ? raw[0] : raw;
                  gpConsent = (value && typeof value === 'object' ? value.optionId : value) || null;
                });

                setCompletionData((prev) => ({
                  ...prev,
                  [patientRecordId]: {
                    formProgress,
                    triageAssigned,
                    hasGuardian: participantData.hasGuardian || Object.keys(formProgress).some((k) => k.endsWith('_guardian')),
                    guardianInviteStatus: participantData.guardianInviteStatus,
                    gpConsent,
                    lastFormActivityAt,
                  },
                }));
              } catch (_) {
                // Soft-fail; keep previous value.
              }
            },
            () => {
              // Soft-fail; keep previous value.
            }
          );
          unsubscribes.push(unsubscribe);
        });
      } catch (_) {
        // Soft-fail; the table can still render without statuses.
      }
    };

    run();

    return () => {
      cancelled = true;
      unsubscribes.forEach((u) => {
        try { u(); } catch (_) {}
      });
    };
  }, [orgId, patientIdsKey]);

  return completionData;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cell Renderers
// ─────────────────────────────────────────────────────────────────────────────
// Shared cell-renderer functions used by the column configurations below.
// Each renderer receives a patient record and (optionally) its completion data,
// and returns a <td> element. Keeping these at module scope lets us reuse them
// across all 7 stage-specific column sets without duplication.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Patient identity cell — name with avatar, plus date of birth and age (in
 * brackets) on a secondary line beneath the name. Replaces the separate DOB
 * column across every stage.
 */
const renderNameCell = (patient) => {
  const dob = getPatientDob(patient);
  const age = getPatientAge(patient);
  const dobLabel = formatDateShort(dob, '');
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <UserIcon className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {getPatientName(patient)}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {dobLabel || '—'}
            {age !== null && age !== '' ? ` (${age} yrs)` : ''}
          </div>
        </div>
      </div>
    </td>
  );
};

/** The date the referral started, with calendar icon — used in every stage.
 * Never the date a SENCO added the pupil to their portal (Tom King,
 * 27 October 2026): see referralStartedAt. */
const renderDateCell = (patient) => (
  <td className="px-6 py-4 whitespace-nowrap">
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <CalendarIcon className="h-4 w-4 text-gray-400" />
      {formatDateShort(referralStartedAt(patient), '-')}
    </div>
  </td>
);

/** Referrer type label (School / Parent / Carer) — stages 1 & 2. */
const renderReferrerCell = (patient) => (
  <td className="px-6 py-4 whitespace-nowrap">
    <ReferrerLabel type={patient.referrerType} />
  </td>
);

/** 
 * Risk stratification tier badge — shown in dashboard tables.
 * Displays the tier badge and "Needs review" indicator if there are
 * pending manual review items that a clinician needs to assess.
 */
const renderRiskTierCell = (patient) => {
  const riskStratification = patient.riskStratification;
  const tierLabel = riskStratification?.tierLabel || null;
  
  // Check if there are any pending manual review items
  // manualReviewCounts tracks items by respondent role (guardian, school_user)
  const manualReviewCounts = riskStratification?.manualReviewCounts || {};
  const totalManualReview = (manualReviewCounts.guardian || 0) + (manualReviewCounts.school_user || 0);
  const hasManualReview = totalManualReview > 0;
  
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      <RiskTierBadge tierLabel={tierLabel} hasManualReview={hasManualReview} />
    </td>
  );
};

/** Chevron arrow (visual affordance for "click to view") — every stage. */
const renderViewArrowCell = () => (
  <td className="px-6 py-4 whitespace-nowrap text-right">
    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
  </td>
);

/**
 * School completion status badge — stage 1 only.
 * Shows whether the school user has completed their referral + HFP forms.
 */
const renderSchoolStatusCell = (patient, completion) => {
  const formProgress = completion?.formProgress || {};
  // Secondary-school referrals skip the HFP (TD-129/TD-137), so exclude it.
  const schoolPhase = patient?.schoolPhase || patient?.school?.schoolType || null;
  // Autism-only referrals drop the ADHD SNAP-IV scale (TD-139).
  const intakeConcern = patient?.intakeConcern || null;
  const status = calculateRoleStatus({
    role: 'school_user',
    formProgress,
    triageAssigned: false, // School never does triage forms.
    schoolPhase,
    intakeConcern,
    // Harbour pupils are judged on the v3 HFP set (hfpFormKeysForRecord).
    patient,
  });
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      <RoleStatusBadge status={status} />
    </td>
  );
};

/**
 * Guardian completion status badge — stage 1 only.
 * Shows whether the guardian has completed their referral + HFP forms.
 * Does NOT include triage form progress (that's a later stage).
 */
const renderGuardianStatusCell = (patient, completion) => {
  const formProgress = completion?.formProgress || {};
  // Secondary-school referrals skip the HFP (TD-129/TD-137), so exclude it.
  const schoolPhase = patient?.schoolPhase || patient?.school?.schoolType || null;
  // Autism-only referrals drop the ADHD SNAP-IV scale (TD-139).
  const intakeConcern = patient?.intakeConcern || null;
  const status = calculateRoleStatus({
    role: 'guardian',
    formProgress,
    // Pass triageAssigned: false so calculateRoleStatus only considers
    // referral + HFP forms (not triage), which is what stage 1 cares about.
    triageAssigned: false,
    hasGuardian: Boolean(completion?.hasGuardian),
    guardianInviteStatus: completion?.guardianInviteStatus || null,
    schoolPhase,
    intakeConcern,
    // Harbour pupils are judged on the v3 HFP set (hfpFormKeysForRecord).
    patient,
  });
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      <RoleStatusBadge status={status} />
    </td>
  );
};

/**
 * Triage forms progress indicator — Forms In Progress stage.
 * Shows a mini progress bar and "X / 7" count so clinicians can see how far
 * the guardian is through the triage questionnaires at a glance. Before the
 * triage forms have been assigned there is nothing to count, so the cell
 * renders a dash rather than a misleading "0 / 7".
 */
const renderTriageProgressCell = (patient, completion) => {
  const triageFormsAssigned =
    Boolean(patient?.triageFormsAssigned) || Boolean(completion?.triageAssigned);
  if (!triageFormsAssigned) {
    return (
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-gray-400 text-xs">—</span>
      </td>
    );
  }
  const formProgress = completion?.formProgress || {};
  const { completed, total } = getTriageFormsProgress(formProgress);
  const progressPercent = total > 0 ? (completed / total) * 100 : 0;

  return (
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center gap-3">
        <div className="flex-1 max-w-[120px]">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                completed === total ? 'bg-green-500' : 'bg-amber-400'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
        <span className="text-sm text-gray-600 tabular-nums whitespace-nowrap">
          {completed} / {total}
        </span>
      </div>
    </td>
  );
};

/**
 * PathwayLabel
 * ------------
 * Plain text label for the assessment pathway chosen during triage acceptance.
 */
const PathwayLabel = ({ pathway }) => {
  // Accepts both the legacy pathwayAllocation values ('autism', 'combined',
  // 'complex') and the tab/PATHWAY constants ('asd', 'adhd') — discharged rows
  // have no accepted review and fall back to the raw tab pathway, which for
  // autism is 'asd'.
  const labels = { autism: 'Autism', asd: 'Autism', adhd: 'ADHD', combined: 'Combined', complex: 'Complex' };
  return (
    <span className="text-sm text-gray-700">
      {labels[pathway] || pathway || '—'}
    </span>
  );
};

// ─── Stage-specific cell renderers (stages 5-7) ─────────────────────────────

/**
 * Assessment pathway badge — Waiting List stage.
 * Reads from the first accepted review in triageReviews.
 */
const renderPathwayCell = (patient, _completion, pathway) => {
  const reviews = pathwayReviews(patient, pathway);
  const acceptedReview = reviews.find((r) => r.accepted);
  // Legacy autism acceptances recorded a chosen pathwayAllocation; the
  // structured ADHD decision doesn't, so fall back to the pathway the decision
  // was made on (else the cell shows a bare "—" on every ADHD acceptance).
  const pathwayValue = acceptedReview?.pathwayAllocation || acceptedReview?.pathway || pathway;
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      <PathwayLabel pathway={pathwayValue} />
    </td>
  );
};

/**
 * "Triaged by" cell — Waiting List stage. Names the clinician(s) who triaged
 * the referral (decider first, then any others involved), rather than framing
 * it as a single decision-maker.
 */
const renderTriagedByCell = (patient, _completion, pathway) => {
  const label = formatCliniciansInvolved(
    getDecisionAttribution(pathwayReviews(patient, pathway)),
  );
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
      {label ? (
        <span className="block max-w-[220px] truncate" title={label}>
          {label}
        </span>
      ) : (
        <span className="text-gray-400 text-xs">—</span>
      )}
    </td>
  );
};

/**
 * "Referred for" cell — the referral's intake concern as a colour-coded badge
 * (ADHD / Autism / ADHD + Autism). Lets a clinician tell a single-concern
 * referral from a combined one at a glance in the queue, without opening it —
 * e.g. an ADHD clinician seeing which of their referrals also involve autism.
 * Renders nothing until the concern is classified (ConcernBadge handles that).
 */
const renderReferredForCell = (patient) => (
  <td className="px-6 py-4 whitespace-nowrap">
    <ConcernBadge intakeConcern={patient?.intakeConcern} />
  </td>
);

/**
 * Assigned clinician name — Waiting List stage.
 * Reads from the first accepted review's assignedClinician field.
 */
const renderAssignedClinicianCell = (patient, _completion, pathway) => {
  const reviews = pathwayReviews(patient, pathway);
  const acceptedReview = reviews.find((r) => r.accepted);
  const name = acceptedReview?.assignedClinician || '—';
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
      {name}
    </td>
  );
};

/**
 * Triage assignment/status — Pending 1st/2nd Review stages (A9 / TD-10, A5 shell / TD-9).
 *
 * Surfaces whether a patient is currently being triaged and by whom, so a second
 * clinician can see a referral already in progress (UI deterrent against a
 * duplicate review). Reads the `patient.triage` assignment state written by the
 * server-enforced assign/lock backend (A5/A7 — TD-28/29). Until that lands the
 * field is absent and every patient renders as "Unassigned", which is correct.
 */
const renderTriageStatusCell = (patient, _completion, pathway) => {
  const triage = pathwayTriage(patient, pathway) || null;
  const inTriage = triage?.status === 'in_triage';
  const triageComplete = triage?.status === 'complete';
  const assignee = triage?.assignedClinicianName;
  const reviewCount = pathwayReviews(patient, pathway).length;
  // A prior review on a triage that is NOT complete means the autism
  // dual-review flow: a triage in progress now is the 2nd review, and an
  // unassigned patient here is awaiting a second clinician. A complete triage
  // (the ADHD single decision, or any settled outcome awaiting its letter)
  // must never read as awaiting a second reviewer (Tom King, 22 Oct).
  const isSecondReview = reviewCount >= 1 && !triageComplete;
  // TDI-395: a decided referral waits on its outcome letter's sign-off, and
  // only the clinician whose decision wrote it can sign it off, so the status
  // names them (staging feedback, 30 Oct).
  const awaitingSignOff = isAwaitingLetterSignOff(patient, pathway);
  const signOffBy = awaitingSignOff ? letterSignOffClinician(patient, pathway) : null;
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      {inTriage ? (
        <span
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
          title={
            assignee
              ? `Locked — ${isSecondReview ? 'second review' : 'triage'} in progress by ${assignee}. Other clinicians cannot start this triage.`
              : 'Locked — triage in progress'
          }
        >
          <LockClosedIcon className="w-3 h-3" aria-hidden="true" />
          {/* Show the triaging clinician's name inline in the label (the full
              "locked" message remains on hover). Falls back to the plain label
              when the assignee name is not available. */}
          {isSecondReview ? '2nd review' : 'In review'}{assignee ? ` — ${assignee}` : ''}
        </span>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          {/* A decided referral in this list is waiting on its outcome letter,
              not on a clinician assignment. */}
          {awaitingSignOff
            ? `Awaiting letter sign-off${signOffBy ? ` by ${signOffBy}` : ''}`
            : triageComplete ? 'Decision recorded' : isSecondReview ? 'Awaiting 2nd reviewer' : 'Unassigned'}
        </span>
      )}
    </td>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Column Configurations
// ─────────────────────────────────────────────────────────────────────────────
// Each sub-stage has its own set of table columns. Columns are objects with:
//   key:        string   — used for sorting (must match a SORT_COMPARATORS key)
//   label:      string   — header text (empty for icon-only columns)
//   sortable:   boolean  — whether the column header is clickable for sorting
//   srOnly:     string   — if set, renders as <span className="sr-only"> (accessibility)
//   renderCell: function — (patient, completion) => <td>...</td>
//
// New stages can be added by defining their column set here and the table will
// automatically adapt — no changes needed to the ReferralTable component.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dwell time on incomplete referrals (LSC-036) — support/admin only.
 * Shows how long since anyone last worked on the referral's forms, so the
 * support team can spot referrals quietly going stale. Falls back to the
 * referral's creation when no form has been touched yet (the creation is the
 * last thing that happened). Read-only visibility only: whether and how to
 * chase is a policy decision that has not been made, so no threshold, colour
 * or action is attached to the number.
 */
const renderLastActivityCell = (patient, completion) => {
  const millis = completion?.lastFormActivityAt
    ?? (patient.createdAt?.toMillis?.() ?? (patient.createdAt ? new Date(patient.createdAt).getTime() : null));
  const label = lastActivityLabel(millis);
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      {label ? (
        <span className="text-sm text-gray-700">{label}</span>
      ) : (
        <span className="text-gray-400 text-xs">—</span>
      )}
    </td>
  );
};

/**
 * Stage 1 — Forms In Progress
 * Some requested form is outstanding (referral/HFP for either respondent, or
 * assigned triage forms). Shows per-role completion badges plus triage-form
 * progress so it is obvious what is still missing, and who from. Support and
 * org admins additionally see the last-activity dwell time (LSC-036).
 */
const COLUMNS_INCOMPLETE_REFERRALS = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'referralStartedAt', label: 'Referral started', sortable: true, renderCell: renderDateCell },
  // { key: 'referrer', label: 'Referred By', sortable: true, renderCell: renderReferrerCell },
  { key: 'schoolStatus', label: 'School', renderCell: renderSchoolStatusCell },
  { key: 'guardianStatus', label: 'Parent / Carer', renderCell: renderGuardianStatusCell },
  { key: 'triageProgress', label: 'Triage Forms', renderCell: renderTriageProgressCell },
  // Not sortable: completion listeners (and thus the activity timestamp) only
  // exist for the rendered page, so a whole-list sort would be a lie.
  { key: 'lastActivity', label: 'Last Activity', renderCell: renderLastActivityCell },
  { key: 'riskTier', label: 'Priority Level', sortable: true, renderCell: renderRiskTierCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

/**
 * Stage 2 — Forms Complete
 * Everything requested has been submitted; the referral is waiting for a
 * provider user to mark it Ready for triage (LSC-038/LSC-044). Shows risk tier
 * (if available from HFP scoring) to help order that decision.
 */
const COLUMNS_COMPLETED_REFERRALS = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'referralStartedAt', label: 'Referral started', sortable: true, renderCell: renderDateCell },
  // { key: 'referrer', label: 'Referred By', sortable: true, renderCell: renderReferrerCell },
  { key: 'riskTier', label: 'Priority Level', sortable: true, renderCell: renderRiskTierCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

/**
 * Stage 4 — Pending 1st Review
 * All triage forms are done. The clinician can now open the Triage Proforma and
 * make a decision. Shows risk tier since risk stratification should be available.
 */
const COLUMNS_PENDING_1ST_REVIEW = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'referralStartedAt', label: 'Referral started', sortable: true, renderCell: renderDateCell },
  { key: 'referredFor', label: 'Referred for', renderCell: renderReferredForCell },
  { key: 'riskTier', label: 'Priority Level', sortable: true, renderCell: renderRiskTierCell },
  { key: 'triageStatus', label: 'Triage Status', renderCell: renderTriageStatusCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

/**
 * Stage 5 — Pending 2nd Review
 * The 1st triage clinician rejected the referral. A second clinician must now
 * provide a second opinion. Shows who the 1st reviewer was plus a notes icon
 * to view their rationale, and the automated risk tier for context.
 */
const COLUMNS_PENDING_2ND_REVIEW = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'referralStartedAt', label: 'Referral started', sortable: true, renderCell: renderDateCell },
  // 1st Reviewer + Rationale intentionally omitted here: a second reviewer must
  // not see who rejected or why until it is time to write the discharge letter
  // (avoids biasing their independent second opinion). Full context lives on the
  // patient overview page (rationale only for the original reviewer).
  { key: 'riskTier', label: 'Priority Level', sortable: true, renderCell: renderRiskTierCell },
  { key: 'triageStatus', label: 'Triage Status', renderCell: renderTriageStatusCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

/**
 * Stage 6 — Waiting List
 * At least one clinician accepted the referral. Shows the pathway they were
 * assigned to, the clinician-determined priority level (styled consistently
 * with the risk tier badges in earlier stages), who recorded the acceptance
 * (and with whom, for MDT decisions), and which clinician will see them next.
 * The separate Risk column is intentionally omitted since the clinician has
 * already made a priority determination.
 */
const COLUMNS_WAITING_LIST = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'referralStartedAt', label: 'Referral started', sortable: true, renderCell: renderDateCell },
  { key: 'pathway', label: 'Pathway', renderCell: renderPathwayCell },
  { key: 'triagedBy', label: 'Triaged by', renderCell: renderTriagedByCell },
  { key: 'assignedClinician', label: 'Assigned To', renderCell: renderAssignedClinicianCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

/**
 * Stage 7 — Discharged Referrals
 * The referral was rejected (both reviewers for the autism dual-review flow;
 * a single MDT decision for the structured ADHD flow). Mirrors the Waiting List
 * layout - patient, referral date, pathway and a single "Triaged by" column
 * naming the clinician(s) involved - rather than splitting out per-reviewer
 * columns, which do not fit the ADHD single-decision flow.
 * The Priority Level (risk tier) column is intentionally omitted here (TD-108):
 * priority is not relevant once a referral has been discharged.
 */
const COLUMNS_DISCHARGED_REFERRALS = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'referralStartedAt', label: 'Referral started', sortable: true, renderCell: renderDateCell },
  { key: 'pathway', label: 'Pathway', renderCell: renderPathwayCell },
  { key: 'triagedBy', label: 'Triaged by', renderCell: renderTriagedByCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

// ─── Admin handover queue cells (support/admin worklist) ─────────────────────

/** Most recent letter sign-off date on the record. */
const renderSignedOffAtCell = (patient) => {
  const signedOffAt = latestSignOffAt(patient);
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <CalendarIcon className="h-4 w-4 text-gray-400" />
        {signedOffAt ? formatDateShort(signedOffAt, '-') : '—'}
      </div>
    </td>
  );
};

/**
 * Send status per signed-off letter — the pending-vs-processed signal the
 * handover queue exists for. Combined referrals show one line per letter.
 * Semantic colour always pairs with an icon and a text label.
 */
const renderLetterStatusCell = (patient) => {
  const entries = signedOffLetterEntries(patient);
  return (
    <td className="px-6 py-4">
      <div className="space-y-1">
        {entries.map((entry) => {
          const sent = Boolean(entry.docs?.sentAt) || (entry.docs?.sharedRoles || []).length > 0;
          const sentDate = entry.docs?.sentAt ? formatDateShort(entry.docs.sentAt, '-') : null;
          // TDI-391: between the letter going out and the EMIS filing being
          // finished the referral is neither awaiting send nor done, and the
          // column said "Sent" for both. The admin team reads this column to
          // find the work still on their desk.
          const handoverDone = isHandoverComplete(patient, entry.pathway);
          return (
            <div key={entry.pathway || 'single'} className="flex items-center gap-1.5 text-xs">
              {entries.length > 1 && (
                <span className="text-gray-500">{PATHWAY_LABELS[entry.pathway] || 'Letter'}:</span>
              )}
              {sent ? (
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${
                  handoverDone
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-sky-200 bg-sky-50 text-sky-700'
                }`}>
                  <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  {handoverDone
                    ? `Sent${sentDate ? ` ${sentDate}` : ''}`
                    : `Sent${sentDate ? ` ${sentDate}` : ''}, EMIS pending`}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-medium text-amber-700">
                  <ExclamationIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Awaiting send
                </span>
              )}
            </div>
          );
        })}
      </div>
    </td>
  );
};

/**
 * GP-sharing consent from the guardian's referral answers (28/07 decision on
 * GP sharing: no automated sending — the consent answer is surfaced here for
 * the admin team's manual process). Shown only where the answer exists.
 */
const GP_CONSENT_LABELS = { yes: 'Yes', no: 'No', not_sure: 'Not sure' };
const renderGpConsentCell = (patient, completion) => {
  const value = completion?.gpConsent || null;
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
      {value
        ? (GP_CONSENT_LABELS[value] || value)
        : <span className="text-gray-400 text-xs">Not answered</span>}
    </td>
  );
};

/**
 * Ready for Admin Handover — the support/admin worklist (LSC feedback, 6 Aug
 * 2026 call): every referral with a signed-off outcome letter, its send
 * status, and the GP-sharing consent needed for the manual record-entry step.
 */
const COLUMNS_READY_FOR_HANDOVER = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'signedOffAt', label: 'Letter Signed Off', sortable: true, renderCell: renderSignedOffAtCell },
  { key: 'referredFor', label: 'Referred For', renderCell: renderReferredForCell },
  { key: 'letterStatus', label: 'Letter Status', renderCell: renderLetterStatusCell },
  { key: 'gpConsent', label: 'GP Sharing Consent', renderCell: renderGpConsentCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

/**
 * Default columns — fallback for any stage without a dedicated column set.
 * Shows a minimal set of columns (name, DOB, date, referrer, arrow).
 */
const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'referralStartedAt', label: 'Referral started', sortable: true, renderCell: renderDateCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

/**
 * Consent withdrawn (TDI-481): the child, when consent was withdrawn, where
 * the referral had got to, and how the withdrawal reached the service.
 */
// Keys are the stages rms_consent_withdrawal.py records, not shorthands: the
// middle one is stored as 'sent_waiting_for_review', and keying it as 'sent'
// left the Stage reached column showing "—" for every referral withdrawn
// while it waited for review.
const WITHDRAWAL_STAGE_LABELS = {
  in_progress: 'Forms in progress',
  sent_waiting_for_review: 'Sent, waiting for review',
  in_review: 'In review',
};
const WITHDRAWAL_ROUTE_LABELS = {
  family: 'Parent or carer, through the portal',
  staff: 'Told the service',
};
const toDate = (value) => {
  if (!value) return null;
  const d = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};
// The table clones each cell as a <td> to align it, so every renderer here
// returns one, like every other column; a bare string crashed the dashboard
// the moment the list was opened (TDI-642).
const withdrawnTextCell = (text) => (
  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{text}</td>
);
const renderWithdrawnOnCell = (patient) => {
  const w = patient?.consentWithdrawal || {};
  const d = toDate(w.reportedAt || w.withdrawnAt);
  return withdrawnTextCell(
    d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—',
  );
};
const renderWithdrawnStageCell = (patient) =>
  withdrawnTextCell(WITHDRAWAL_STAGE_LABELS[patient?.consentWithdrawal?.stage] || '—');
const renderWithdrawnRouteCell = (patient) => {
  const w = patient?.consentWithdrawal || {};
  const base = WITHDRAWAL_ROUTE_LABELS[w.route] || '—';
  return withdrawnTextCell(w.route === 'staff' && w.method ? `${base} (${w.method})` : base);
};
const COLUMNS_CONSENT_WITHDRAWN = [
  { key: 'name', label: 'Patient', sortable: true, renderCell: renderNameCell },
  { key: 'withdrawnOn', label: 'Consent withdrawn', renderCell: renderWithdrawnOnCell },
  { key: 'withdrawnStage', label: 'Stage reached', renderCell: renderWithdrawnStageCell },
  { key: 'withdrawnRoute', label: 'How we were told', renderCell: renderWithdrawnRouteCell },
  { key: 'view', label: '', srOnly: 'View', renderCell: renderViewArrowCell },
];

/** Map from sub-stage ID → column config. Falls back to DEFAULT_COLUMNS. */
const STAGE_COLUMN_CONFIGS = {
  'consent-withdrawn': COLUMNS_CONSENT_WITHDRAWN,
  'incomplete-referral-forms': COLUMNS_INCOMPLETE_REFERRALS,
  'completed-referral-forms': COLUMNS_COMPLETED_REFERRALS,
  'pending-1st-review': COLUMNS_PENDING_1ST_REVIEW,
  'pending-2nd-review': COLUMNS_PENDING_2ND_REVIEW,
  'waiting-list': COLUMNS_WAITING_LIST,
  'discharged-referrals': COLUMNS_DISCHARGED_REFERRALS,
  'ready-for-handover': COLUMNS_READY_FOR_HANDOVER,
};

const DEFAULT_SORT_FALLBACK = { key: 'referralStartedAt', direction: 'descending' };

/**
 * Default sort per sub-stage. Stages with a risk/priority column default to
 * sorting by severity (descending = Urgent first). Stages without risk data
 * fall back to newest-first by referral date.
 */
const STAGE_DEFAULT_SORT = {
  'incomplete-referral-forms': { key: 'riskTier', direction: 'descending' },
  'completed-referral-forms': { key: 'riskTier', direction: 'descending' },
  'pending-1st-review': { key: 'riskTier', direction: 'descending' },
  'pending-2nd-review': { key: 'riskTier', direction: 'descending' },
  // Waiting List no longer surfaces a Priority Level column; sort by referral date.
  'waiting-list': { key: 'referralStartedAt', direction: 'descending' },
  // Discharged has no Priority Level column (TD-108); sort by referral date instead.
  'discharged-referrals': { key: 'referralStartedAt', direction: 'descending' },
  // Handover: most recently signed-off letters first.
  'ready-for-handover': { key: 'signedOffAt', direction: 'descending' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Sort Comparators
// ─────────────────────────────────────────────────────────────────────────────
// Standard comparison functions for sortable columns. Keyed by column `key`.
// The ReferralTable uses these to sort the patient list when a header is clicked.
// Only columns that have an entry here AND `sortable: true` will be sortable.
// ─────────────────────────────────────────────────────────────────────────────
// Maps risk tier labels and clinician-determined priority levels to a numeric
// severity score for sorting. Higher = more urgent. Records without a tier
// sort to the bottom (score 0).
const RISK_TIER_SORT_ORDER = {
  'Urgent Priority': 4,
  'High Priority': 3,
  'Medium Priority': 2,
  'Standard Priority': 1,
  // Legacy data compatibility: some historical records still store "Risk" labels.
  'Urgent Risk': 4,
  'High Risk': 3,
  'Medium Risk': 2,
  'Standard Risk': 1,
};
const PRIORITY_SORT_ORDER = { urgent: 4, high: 3, medium: 2, standard: 1 };

const SORT_COMPARATORS = {
  name: (a, b) =>
    getPatientName(a).toLowerCase().localeCompare(getPatientName(b).toLowerCase()),
  dob: (a, b) => {
    const aDate = getPatientDob(a) ? new Date(getPatientDob(a)) : new Date(0);
    const bDate = getPatientDob(b) ? new Date(getPatientDob(b)) : new Date(0);
    return aDate - bDate;
  },
  // Sorts by the same value the column shows (the referral's own start
  // date), so the order always matches what is on screen.
  referralStartedAt: (a, b) => {
    const toDate = (patient) => {
      const value = referralStartedAt(patient);
      return value?.toDate?.() || (value ? new Date(value) : new Date(0));
    };
    return toDate(a) - toDate(b);
  },
  referrer: (a, b) =>
    (a.referrerType || '').toLowerCase().localeCompare((b.referrerType || '').toLowerCase()),
  riskTier: (a, b) => {
    const aScore = RISK_TIER_SORT_ORDER[a.riskStratification?.tierLabel] || 0;
    const bScore = RISK_TIER_SORT_ORDER[b.riskStratification?.tierLabel] || 0;
    return aScore - bScore;
  },
  // Pathway-aware (TD-155): a combined referral's accepted review lives under
  // triagePathways.{pathway}; pathwayReviews falls back to the legacy
  // top-level triageReviews for single-concern and pre-split records.
  priority: (a, b, pathway) => {
    const aReview = pathwayReviews(a, pathway).find((r) => r.accepted);
    const bReview = pathwayReviews(b, pathway).find((r) => r.accepted);
    const aScore = PRIORITY_SORT_ORDER[aReview?.priorityLevel] || 0;
    const bScore = PRIORITY_SORT_ORDER[bReview?.priorityLevel] || 0;
    return aScore - bScore;
  },
  signedOffAt: (a, b) =>
    (latestSignOffAt(a)?.getTime() || 0) - (latestSignOffAt(b)?.getTime() || 0),
};

// ─────────────────────────────────────────────────────────────────────────────
// ReferralTable (Generic)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ReferralTable
 * -------------
 * A configurable table component that renders a paginated, sortable list of
 * referrals. The columns displayed are driven by the `columns` prop, so each
 * sub-stage can show a different set of columns without duplicating the table
 * shell (sorting, pagination, empty state, row click, etc.).
 *
 * Props:
 *   columns                   — array of column config objects (see above)
 *   patients                  — array of patient records for this sub-stage
 *   completionDataByPatientId — form-progress map keyed by patient ID
 *   onRowClick                — callback(patientId) for row click navigation
 *   subStageId                — the current sub-stage ID (resets pagination on change)
 *   defaultSort               — { key, direction } to use as the initial sort when
 *                                the sub-stage changes. Defaults to createdAt descending.
 *   emptyMessage              — string shown when there are no patients
 *   pathway                   — the active condition pathway ('adhd' | 'asd');
 *                                cell renderers and sorting read a combined
 *                                referral's per-pathway triage state with it (TD-155)
 */
function ReferralTable({
  columns,
  patients,
  completionDataByPatientId,
  onRowClick,
  onRemoveClick,
  subStageId,
  defaultSort = DEFAULT_SORT_FALLBACK,
  emptyMessage = 'No referrals in this stage',
  pathway,
}) {
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [currentPage, setCurrentPage] = useState(1);
  const patientsPerPage = 10;

  // Reset sort order and pagination when the sub-stage changes.
  useEffect(() => {
    setSortConfig(defaultSort);
    setCurrentPage(1);
  }, [subStageId, defaultSort]);

  // Reset to page 1 when the patient list grows/shrinks (live Firestore updates).
  useEffect(() => {
    setCurrentPage(1);
  }, [patients.length]);

  // Toggle sort direction on the same column, or switch to a new column ascending.
  const requestSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  }, []);

  // Render a small sort-direction icon next to the active column header.
  const getSortIcon = useCallback((columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'ascending'
      ? <ChevronUpIcon className="w-4 h-4 ml-1 text-white" />
      : <ChevronDownIcon className="w-4 h-4 ml-1 text-white" />;
  }, [sortConfig]);

  // Sort patients using the comparator for the active column key.
  // Referrals waiting on a letter sign-off stay pinned above the rest, in the
  // chosen order within each group, so they are not lost in the list.
  const sortedPatients = useMemo(() => {
    const result = [...patients];
    const comparator = SORT_COMPARATORS[sortConfig.key];
    if (comparator) {
      result.sort((a, b) => {
        const cmp = comparator(a, b, pathway);
        return sortConfig.direction === 'ascending' ? cmp : -cmp;
      });
    }
    return pinAwaitingLetterSignOff(result, pathway);
  }, [patients, sortConfig, pathway]);

  // Pagination calculations.
  const indexOfLast = currentPage * patientsPerPage;
  const indexOfFirst = indexOfLast - patientsPerPage;
  const currentPatients = sortedPatients.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedPatients.length / patientsPerPage);

  // Empty state when there are no patients in this sub-stage.
  if (patients.length === 0) {
    return (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600">{emptyMessage}</p>
          </div>
    );
  }

  return (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
        {/* Column headers — dynamically rendered from the columns config.
            All columns are left-aligned (the trailing chevron stays right). */}
              <thead className="bg-primary">
                <tr>
            {columns.map((col) => (
                  <th
                key={col.key}
                    scope="col"
                className={`px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider text-left ${col.sortable ? 'cursor-pointer hover:bg-primary/80 transition-colors' : ''}`}
                onClick={col.sortable ? () => requestSort(col.key) : undefined}
              >
                {col.srOnly ? (
                  <span className="sr-only">{col.srOnly}</span>
                ) : (
                  <div className="flex items-center">
                    {col.label}
                    {col.sortable && getSortIcon(col.key)}
                    </div>
                )}
                  </th>
            ))}
            {onRemoveClick && (
              <th scope="col" className="px-3 py-3 text-xs font-semibold text-white uppercase tracking-wider text-center">
                <span className="sr-only">Actions</span>
              </th>
            )}
                </tr>
              </thead>

        {/* Table body — every column left-aligned (the trailing chevron keeps
            its text-right). We override each cell's <td> className uniformly
            without touching individual cell renderers. */}
              <tbody className="bg-white divide-y divide-gray-100">
                {currentPatients.map((patient) => {
            const completion = completionDataByPatientId?.[patient.id] || null;
                  return (
                    <tr
                      key={patient.id}
                onClick={() => onRowClick(patient.id)}
                      className={`${isAwaitingLetterSignOff(patient, pathway) ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
                    >
                {columns.map((col, colIndex) => (
                  <React.Fragment key={col.key}>
                    {colIndex === 0
                      ? col.renderCell(patient, completion, pathway)
                      : (() => {
                          // Clone the <td> returned by renderCell and left-align it
                          // (preserving the chevron's text-right).
                          const cell = col.renderCell(patient, completion, pathway);
                          if (!cell) return cell;
                          const existing = cell.props.className || '';
                          const aligned = existing.includes('text-right')
                            ? existing
                            : `${existing.replace(/\btext-center\b/g, '').trim()} text-left`;
                          return React.cloneElement(cell, { className: aligned });
                        })()
                    }
                  </React.Fragment>
                ))}
                {onRemoveClick && (
                  <td className="px-3 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveClick(patient);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      aria-label="Remove referral"
                      title="Remove referral"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </td>
                )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

      {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center px-6 py-3 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{indexOfFirst + 1}</span> to{' '}
            <span className="font-medium">{Math.min(indexOfLast, sortedPatients.length)}</span> of{' '}
            <span className="font-medium">{sortedPatients.length}</span> referrals
                </div>
                <div className="flex items-center gap-2">
                  <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === totalPages
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
  );
}

/**
 * The patient records a clinician's dashboard lists, from the snapshot the
 * gated query returns: every referred child, and nobody else.
 *
 * A child reaches the triage app when they are referred, never before (Tom
 * King, 27 October 2026). A pupil a SENCO has listed in their portal, or who
 * is only on the SEND pathway, has not been referred to BTH and is no
 * clinician's work — listing them made the service's incoming queue look
 * fuller than it is and put children in front of clinicians who had no
 * referral to read. The rule reads each record's own referral state
 * (isSenOnlyRecord), so it needs no backfill and no new field.
 *
 * Access control is the server gate and the security rules (TDI-467), which
 * already exclude records the family has not granted; this filter is belt and
 * braces, so a pupil mis-flagged by a future write path still never surfaces.
 *
 * The sort is here because TDI-467's equality filter replaced the query's
 * orderBy — a different-field orderBy would need a composite index for no
 * gain, since every record is loaded anyway.
 *
 * @param {Array} docs - QuerySnapshot docs
 * @returns {Array} patient records, newest first
 */
export function providerDashboardRecords(docs) {
  return (docs || [])
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((record) => !isSenOnlyRecord(record))
    .sort((a, b) => {
      const at = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bt = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return bt - at;
    });
}

export default function RmsProviderDashboard() {
  const navigate = useNavigate();
  const { claims, user } = useAuth();
  const orgId = claims?.rmsOrgId;

  // TD-27: a clinician's specialism scopes the queue to patients whose intake
  // concern they can triage. A 'both' specialism, or none (admins/support), sees
  // everything. The server gate (rmsStartTriage / rmsSubmitTriageDecision) is the
  // authoritative enforcement; this just keeps the queue relevant.
  const specialism = claims?.specialism;
  const isSpecialismScoped =
    !!specialism && specialism !== CLINICIAN_SPECIALISM.BOTH;

  // C8: pathway tabs (ADHD team / autism team). A both-specialism clinician (or
  // admin/support) sees both tabs; a single-specialism clinician sees only their
  // pathway. The active tab scopes the queue + drives per-pathway classification,
  // and is carried into the patient route so triage acts on the right pathway.
  const pathwayTabs = useMemo(() => visiblePathways(specialism), [specialism]);
  const hasPathwayTabs = pathwayTabs.length > 1; // clinician covers both pathways
  // Persist the active pathway tab for the browser-tab session so it survives
  // opening a patient and navigating back. The effect below re-validates it
  // against the pathways this clinician can see, so a stale/foreign value (e.g.
  // a different-specialism user on a shared tab) self-heals to their first tab.
  const [activePathway, setActivePathway] = useSessionState(RMS_DASHBOARD_KEYS.pathway, pathwayTabs[0]);
  useEffect(() => {
    if (!pathwayTabs.includes(activePathway)) setActivePathway(pathwayTabs[0]);
  }, [pathwayTabs, activePathway, setActivePathway]);

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * selectedSubStage
   * ----------------
   * Tracks which sub-stage tab is currently active in the sidebar. Persisted for
   * the browser-tab session (useSessionState) so opening a patient and coming
   * back keeps the clinician on the tab they left — e.g. "Pending 1st Review" —
   * instead of resetting to the default. Defaults to the first sub-stage so the
   * page isn't blank on initial load.
   */
  const [selectedSubStage, setSelectedSubStage] = useSessionState(
    RMS_DASHBOARD_KEYS.subStage,
    DEFAULT_SUBSTAGE,
  );
  // Sub-stage tabs visible to this user: Second Review belongs to the autism
  // pathway, so it is absent while the ADHD tab is in view (TDI-399), and only
  // Support/Org. Admins see the admin-handover queue. Switching to the ADHD tab
  // while sitting on Second Review coerces the selection to a visible list.
  const visibleSubIds = useMemo(
    () => visibleSubStageIds(activePathway, claims),
    [activePathway, claims],
  );
  // Heal a restored sub-stage that a newer build no longer defines, or that is
  // not visible to this clinician's specialism, so we never sit on a hidden tab
  // or one that resolves to an empty table.
  useEffect(() => {
    const coerced = coerceSubStage(selectedSubStage, visibleSubIds, DEFAULT_SUBSTAGE);
    if (coerced !== selectedSubStage) setSelectedSubStage(coerced);
  }, [selectedSubStage, setSelectedSubStage, visibleSubIds]);

  // Show the ADHD/autism team tabs only on triage/post-triage stages (the
  // pre-triage stages are pre-classification and not split by pathway).
  const showPathwayTabs = hasPathwayTabs && PATHWAY_SCOPED_SUBSTAGES.has(selectedSubStage);

  // Dev-only: state for the demo data generation dialog
  const [showDemoDialog, setShowDemoDialog] = useState(false);

  // Patient removal: tracks which patient the user has clicked "remove" on.
  // When non-null, the RmsPatientRemoveForm dialog is rendered.
  const [removingPatient, setRemovingPatient] = useState(null);

  // Finalise provider onboarding (idempotent).
  // After completing password + MFA setup, the user may arrive here directly
  // from SetUpMFA without going back through RmsProviderSignIn. This ensures
  // the provider profile transitions from 'invited' → 'active' regardless of
  // the navigation path used to reach the dashboard.
  useEffect(() => {
    if (!orgId || claims?.accountType !== 'RMS Provider User') return;
    const completeSetup = httpsCallable(functions, 'rmsCompleteProviderSetup');
    completeSetup({ orgId }).catch((err) => {
      console.warn('[RmsProviderDashboard] rmsCompleteProviderSetup (non-blocking):', err);
    });
  }, [orgId, claims?.accountType]);

  // Subscribe to patient records collection. TDI-467: clinical access is
  // consent-gated — the query asks only for records whose referral is active
  // with consent standing (clinicalAccessGranted), which is also what the
  // security rules enforce for provider reads, so the query must match the
  // rule or every dashboard read is denied. SEND-pathway-only pupils never
  // carry the grant and so never reach any provider list.
  useEffect(() => {
    if (!orgId) return;

    const patientsRef = collection(db, 'rms_organisations', orgId, 'patientRecords');
    const q = query(patientsRef, where('clinicalAccessGranted', '==', true));

    // TDI-481: a withdrawn referral has lost its clinical access grant, so
    // the query above never returns it. The non-clinical side reads them
    // through a second query on the stop reason, which is exactly what the
    // rules allow them and nobody else. The two result sets are merged by
    // id: a referral the family withdrew and then restored carries no stop
    // reason, so it comes back only through the first query, as it should.
    const withdrawnQ = isNonClinicalProvider(claims)
      ? query(patientsRef, where('referralPathway.stopReason', '==', 'consent_withdrawn'))
      : null;

    const byQuery = { active: [], withdrawn: [] };
    const publish = () => {
      const seen = new Set();
      const merged = [];
      for (const list of [byQuery.active, byQuery.withdrawn]) {
        for (const doc of list) {
          if (seen.has(doc.id)) continue;
          seen.add(doc.id);
          merged.push(doc);
        }
      }
      setPatients(providerDashboardRecords(merged));
      setLoading(false);
    };

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        byQuery.active = snapshot.docs;
        publish();
      },
      (err) => {
        console.error('[RmsProviderDashboard] Error loading patients:', err);
        setError('Failed to load patient records');
        setLoading(false);
      }
    );
    const unsubscribeWithdrawn = withdrawnQ
      ? onSnapshot(
        withdrawnQ,
        (snapshot) => {
          byQuery.withdrawn = snapshot.docs;
          publish();
        },
        (err) => {
          // The main list still works without this one; say so in the console
          // rather than blanking the dashboard.
          console.error('[RmsProviderDashboard] Error loading withdrawn referrals:', err);
        }
      )
      : null;

    return () => {
      unsubscribe();
      if (unsubscribeWithdrawn) unsubscribeWithdrawn();
    };
  }, [orgId, claims]);

  /**
   * Derive the currently-active sub-stage config object from REFERRAL_STAGES.
   * Used to render the page header (label + description) for the selected tab.
   */
  const activeSubStage = useMemo(() => {
    for (const stage of REFERRAL_STAGES) {
      const found = stage.subStages.find((ss) => ss.id === selectedSubStage);
      if (found) return found;
    }
    return null;
  }, [selectedSubStage]);

  /**
   * filteredPatients
   * ----------------
   * Applies the search filter (by patient name) across the full patient set.
   * Stage classification happens downstream — the search is global across all stages.
   */
  const filteredPatients = useMemo(() => {
    // Specialism gate first (TD-27) + search. Pathway scoping is applied per-stage
    // during bucketing (C8) so it only affects triage/post-triage stages.
    let result = filterPatientsBySpecialism(patients, specialism);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((p) => getPatientName(p).toLowerCase().includes(term));
    }
    return result;
  }, [patients, specialism, searchTerm]);

  /**
   * Status computation — subscribes to formInstances for all search-filtered patients
   * so we have the data needed to classify every referral into its correct stage.
   */
  const recordsById = useMemo(
    () => Object.fromEntries(filteredPatients.map((p) => [p.id, p])),
    [filteredPatients],
  );
  const completionDataByPatientId = useCompletionDataForPatients({
    orgId,
    patientIds: filteredPatients.map((p) => p.id),
    recordsById,
  });

  /**
   * patientsByStage
   * ---------------
   * Classifies every (search-filtered) patient into exactly one sub-stage bucket
   * using the classification logic in referralStatus.js. The resulting map is keyed
   * by sub-stage ID (e.g. 'incomplete-referral-forms') and contains the array of patient
   * records for that stage.
   *
   * This drives both:
   *   - The count badges in the sidebar navigation
   *   - The table contents for the currently selected sub-stage
   */
  const patientsByStage = useMemo(() => {
    // Initialise every sub-stage bucket as an empty array.
    const buckets = {};
    for (const stage of REFERRAL_STAGES) {
      for (const sub of stage.subStages) {
        buckets[sub.id] = [];
      }
    }

    // Classify each patient into the appropriate bucket.
    for (const patient of filteredPatients) {
      const completion = completionDataByPatientId?.[patient.id] || null;
      const stageId = classifyReferralStage(patient, completion, activePathway);
      // A referral belongs to exactly one list (Tom King, 23 Sept). Admin
      // Handover is no longer layered on top of the triage-complete buckets:
      // classifyReferralStage routes a signed-off-but-unsent letter there, and
      // sending it moves the referral on to Accepted or Not Accepted.
      // Pathway scoping (C8): on triage/post-triage stages, only show patients on
      // the active pathway's team (combined appears under both). Pre-triage stages
      // are not split. Only applies when the clinician covers both pathways.
      if (
        hasPathwayTabs &&
        PATHWAY_SCOPED_SUBSTAGES.has(stageId) &&
        !patientInPathway(patient, activePathway)
      ) {
        continue;
      }
      // LSC-040: once a referral is picked up it disappears from other
      // clinicians' triage lists entirely — no locked rows carrying the
      // assignee's name, so a later reviewer cannot be biased by who is (or
      // was) working a case. Support and Org. Admins keep full visibility of
      // assignments; the clinician's own picked-up referral stays visible.
      //
      // The assignment, not the lock status, is what decides this (Tom King,
      // 29 Sept: "the patient should still be assigned to that clinician").
      // A referral whose decision is recorded but whose letter is not yet
      // signed off stays in Pending Review and keeps its assignee, so keying
      // on status === 'in_triage' would have put every clinician's unfinished
      // work back into everyone else's list. A first autism rejection clears
      // the assignee, so a pending second review stays visible to all — which
      // is the point of it.
      if (
        claims?.userType === 'RMS Clinician' &&
        (stageId === 'pending-1st-review' || stageId === 'pending-2nd-review')
      ) {
        const lockPathway = resolveActingPathway(patient, activePathway, specialism);
        if (isAssignedToAnotherClinician(patient, lockPathway, user?.uid)) {
          continue;
        }
      }
      if (buckets[stageId]) {
        buckets[stageId].push(patient);
      } else {
        // Fallback: if the stage ID isn't recognised (e.g. stages 5-7 placeholder),
        // put the patient in the closest matching bucket.
        buckets['incomplete-referral-forms']?.push(patient);
      }
    }

    return buckets;
  }, [filteredPatients, completionDataByPatientId, activePathway, hasPathwayTabs,
    claims?.userType, specialism, user?.uid]);

  /** Specialism-scoped total (ignores search + pathway) for the sidebar summary. */
  const scopedTotal = useMemo(
    () => filterPatientsBySpecialism(patients, specialism).length,
    [patients, specialism],
  );

  /** The patients to display in the currently selected sub-stage table. */
  const currentStagePatients = patientsByStage[selectedSubStage] || [];

  /**
   * Column configuration for the currently selected sub-stage table.
   * Display-only gate: some orgs hide the automated risk-stratification tier
   * (still computed/stored server-side), so drop the Priority Level column
   * entirely for them. See src/rms/constants/orgFeatures.js.
   */
  const riskStratificationVisible = isRiskStratificationVisible(orgId);
  // Every provider user sees the same columns (Tom King, 23 Sept): the dwell
  // time on an incoming referral is useful to whoever is looking, and hiding it
  // from clinicians only meant two people describing different screens.
  const currentColumns = useMemo(() => {
    let columns = STAGE_COLUMN_CONFIGS[selectedSubStage] || DEFAULT_COLUMNS;
    if (!riskStratificationVisible) {
      columns = columns.filter((column) => column.key !== 'riskTier');
    }
    return columns;
  }, [selectedSubStage, riskStratificationVisible]);

  /**
   * Default sort for the currently selected sub-stage (risk/priority first
   * where applicable). When the org hides risk stratification the tables must
   * not silently ORDER by the hidden tier either, so fall back to referral date.
   */
  const stageDefaultSort = STAGE_DEFAULT_SORT[selectedSubStage] || DEFAULT_SORT_FALLBACK;
  const currentDefaultSort =
    !riskStratificationVisible && stageDefaultSort.key === 'riskTier'
      ? DEFAULT_SORT_FALLBACK
      : stageDefaultSort;

  // Navigate to the detailed patient overview page, carrying the active pathway
  // so a combined referral is triaged on the tab's pathway (C8).
  const handleRowClick = (patientId) => {
    // Handover rows open the guided Admin Handover screen (TDI-386), carrying
    // the tab's pathway so a combined referral's row opens on ITS pathway's
    // letter and documents rather than falling back to ADHD.
    if (selectedSubStage === 'ready-for-handover') {
      navigate(`/rms/provider/patient/${patientId}/handover${activePathway ? `?pathway=${activePathway}` : ''}`);
      return;
    }
    // A decided referral still waiting on its letter's sign-off reopens
    // where the clinician left off: the letter review (TDI-395).
    if (selectedSubStage === 'pending-1st-review') {
      const patient = patients.find((p) => p.id === patientId);
      const triage = patient ? pathwayTriage(patient, activePathway) : null;
      if (triage?.status === 'complete' && !hasSignedOffLetter(patient, activePathway)) {
        navigate(`/rms/provider/patient/${patientId}/review-document${activePathway ? `?pathway=${activePathway}` : ''}`);
        return;
      }
    }
    navigate(`/rms/provider/patient/${patientId}?pathway=${activePathway}`);
  };

  // Breadcrumbs for the top navigation bar.
  const breadcrumbs = [{ label: 'Home', path: null }];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />

      {/*
        Main layout: side-by-side sidebar + content area.
        The sidebar is fixed-width and scrollable independently of the content pane.
      */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ────────────────────────────────────────────────────────────────────
            SIDEBAR NAVIGATION
            ────────────────────────────────────────────────────────────────────
            Renders the three overarching stages with their sub-stage items.
            All sections are always expanded (no accordion toggle).
            Styling follows the HfpReportDialog sidebar pattern but adapted
            for a full-page layout instead of a dialog.
        */}
        <aside className="w-[280px] flex-shrink-0 border-r border-gray-200 bg-gradient-to-b from-primary/5 to-primary/10 flex flex-col">
          {/* Scrollable nav area */}
          <div className="flex-1 overflow-y-auto p-4">
            <nav className="space-y-2">
              {REFERRAL_STAGES
                // Hide a whole section when none of its sub-stages are visible
                // to this user (e.g. STOPPED for clinicians). The section and
                // its items both read visibleSubIds, the same list the tab
                // healing uses, so a heading can never show with nothing under
                // it (TDI-642).
                .filter((stage) => stage.subStages.some((sub) => visibleSubIds.includes(sub.id)))
                .map((stage) => (
                <div key={stage.id} className="overflow-hidden">
                  {/*
                    Section header — always visible, non-clickable.
                    Mirrors the HfpReportDialog section header style:
                    uppercase, semibold, deep-purple (#4a2c5a) text.
                  */}
                  <div className="w-full flex items-center px-3 py-2.5 text-left font-semibold text-[#4a2c5a] text-sm tracking-wide">
                    <span>{stage.title}</span>
                  </div>

                  {/* Sub-stage items — always visible (no accordion), minus any
                      hidden for the pathway in view (Second Review is autism
                      only) or role (Consent withdrawn is for the non-clinical
                      side only). */}
                  <div className="pl-2 pr-1 py-1 space-y-0.5">
                    {stage.subStages
                      .filter((sub) => visibleSubIds.includes(sub.id))
                      .map((sub) => {
                      const isActive = selectedSubStage === sub.id;
                      const count = patientsByStage[sub.id]?.length || 0;
                      return (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={() => setSelectedSubStage(sub.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 flex items-center justify-between ${
                            isActive
                              ? 'bg-white shadow-sm border border-gray-200 text-[#4a2c5a] font-medium'
                              : 'text-gray-600 hover:bg-white/70 hover:text-gray-800'
                          }`}
                        >
                          <span>{subStageLabelForSpecialism(sub.id, sub.label, specialism)}</span>
                          {/* Show the count of referrals in this sub-stage as a small badge */}
                          <span className={`text-xs tabular-nums ${
                            isActive ? 'text-[#4a2c5a]' : 'text-gray-400'
                          }`}>
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/*
            Sidebar footer — shows total referral count as a quick summary.
            Sits at the bottom of the sidebar, always visible.
          */}
          <div className="border-t border-gray-200 bg-white/80 p-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Summary
            </div>
            <div className="text-sm text-gray-700">
              <span className="font-semibold tabular-nums">{scopedTotal}</span>{' '}
              {isSpecialismScoped ? 'referral' : 'total referral'}{scopedTotal !== 1 ? 's' : ''}
            </div>
            {isSpecialismScoped && (
              <div className="mt-1 text-xs text-gray-500">
                Showing {CLINICIAN_SPECIALISM_LABELS[specialism] || specialism} referrals only
              </div>
            )}
          </div>
        </aside>

        {/* ────────────────────────────────────────────────────────────────────
            MAIN CONTENT PANE
            ────────────────────────────────────────────────────────────────────
            Displays the page header (sub-stage label + description), search bar,
            and the referral table for the currently selected sub-stage.
        */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="px-6 sm:px-8 lg:px-10 py-8">
            {/* C8: pathway tabs — the active tab scopes the queue to a team
                (ADHD / autism). Shown only when the clinician covers both. */}
            {showPathwayTabs && (
              <div className="mb-6 flex gap-1 border-b border-gray-200" role="tablist">
                {pathwayTabs.map((p) => (
                  <button
                    key={p}
                    type="button"
                    role="tab"
                    aria-selected={activePathway === p}
                    onClick={() => setActivePathway(p)}
                    className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                      activePathway === p
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {PATHWAY_LABELS[p]}
                  </button>
                ))}
              </div>
            )}
            {/* Page header — changes dynamically based on selected sub-stage */}
            <div className="mb-6 text-left">
              <h1 className="text-2xl font-bold text-gray-900">
                {activeSubStage
                  ? subStageLabelForSpecialism(activeSubStage.id, activeSubStage.label, specialism)
                  : 'Referral Management'}
              </h1>
              {activeSubStage?.description && (
                <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                  {activeSubStage.description}
                </p>
              )}
              {isSpecialismScoped && (
                <p className="text-sm text-primary/80 mt-2 max-w-2xl">
                  This queue is scoped to your specialism
                  {' '}(<span className="font-bold">{CLINICIAN_SPECIALISM_LABELS[specialism] || specialism}</span>).
                  Referrals concerning the other pathway are handled by that team.
                </p>
              )}
            </div>

            {/* Search bar + patient count */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="relative flex-grow max-w-md">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by patient name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  />
                </div>

                {/* Dev-only: Generate demo referrals button */}
                {isDev && (
                  <button
                    type="button"
                    onClick={() => setShowDemoDialog(true)}
                    title="Generate demo referrals (dev only)"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    <LightningBoltIcon className="w-4 h-4" />
                    Generate Demo Data
                  </button>
                )}
              </div>
            </div>

            {/* Loading state */}
            {loading && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <RefreshIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
                <p className="text-gray-600">Loading referrals...</p>
              </div>
            )}

            {/* Error state */}
            {error && !loading && (
              <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {/* Referral table — columns and data are driven by the selected sub-stage */}
            {!loading && !error && (
              <ReferralTable
                columns={currentColumns}
                patients={currentStagePatients}
                completionDataByPatientId={completionDataByPatientId}
                onRowClick={handleRowClick}
                onRemoveClick={(patient) => setRemovingPatient(patient)}
                subStageId={selectedSubStage}
                defaultSort={currentDefaultSort}
                emptyMessage={`No referrals in "${activeSubStage ? subStageLabelForSpecialism(activeSubStage.id, activeSubStage.label, specialism) : 'this stage'}"`}
                pathway={activePathway}
              />
            )}
          </div>
        </main>
      </div>

      {/* Dev-only: Demo data generation dialog */}
      {isDev && (
        <GenerateDemoReferralsDialog
          isOpen={showDemoDialog}
          onOpenChange={setShowDemoDialog}
          orgId={orgId}
        />
      )}

      {/* Patient removal confirmation dialog — rendered unconditionally so Radix
         Presence can animate the exit transition when `open` flips to false. */}
      <RmsPatientRemoveForm
        isOpen={!!removingPatient}
        onClose={() => setRemovingPatient(null)}
        patient={removingPatient}
        orgId={orgId}
        onSuccess={(removedId) => {
          // Optimistic update: remove the patient from local state immediately.
          // The real-time onSnapshot listener will confirm on the next cycle.
          setPatients((prev) => prev.filter((p) => p.id !== removedId));
        }}
      />
    </div>
  );
}
